import Api from '../../api'
import { all, takeEvery, takeLatest, call, put } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import { RECEIVE_FEED_ITEMS } from 'portal-modules/community-feed/redux/modules/feed'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { handleResponseError } from 'portal-modules/common/redux/create-request-saga'
import { POSTED_PAYMENT } from 'portal-modules/payments/redux/modules/payments'

// action types
export const REQUEST_EVENTS_LIST = 'rp/events/REQUEST_EVENTS_LIST'
export const RECEIVE_EVENTS_LIST = 'rp/events/RECEIVE_EVENTS_LIST'
export const SAVE_EVENT = 'rp/events/SAVE_EVENT'
export const EVENT_SAVED = 'rp/events/EVENT_SAVED'
export const UPDATE_EVENT_ATTENDANCE = 'rp/events/UPDATE_EVENT_ATTENDANCE'
export const EVENT_ATTENDANCE_UPDATED = 'rp/events/EVENT_ATTENDANCE_UPDATED'
export const DELETE_EVENT = 'rp/events/DELETE_EVENT'
export const EVENT_DELETED = 'rp/events/EVENT_DELETED'
export const EVENT_AUTH_CAPTURE_PAYMENT = 'rp/events/EVENT_AUTH_CAPTURE_PAYMENT'
export const EVENT_PAYMENT_UPDATE = 'rp/events/EVENT_PAYMENT_UPDATE'
export const REQUEST_EVENTS_LIST_WITH_SETTINGS =
  'rp/events/REQUEST_EVENTS_LIST_WITH_SETTINGS'

// action creators
export const requestEventsList = () => ({
  type: REQUEST_EVENTS_LIST,
})

export const requestEventsListWithSettings = () => ({
  type: REQUEST_EVENTS_LIST_WITH_SETTINGS,
})

export const saveEvent = createFormAction(SAVE_EVENT)

export const updateEventAttendance = payload => ({
  type: UPDATE_EVENT_ATTENDANCE,
  payload,
})

export const deleteEvent = (payload, callback) => ({
  type: DELETE_EVENT,
  callback,
  payload,
})

export const requestAuthCaptureEventPayment = payload => ({
  type: EVENT_AUTH_CAPTURE_PAYMENT,
  payload,
})

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_FEED_ITEMS:
      return payload?.events && Object.keys(payload?.events).length
        ? {
            ...state,
            ...payload.events,
          }
        : state
    case RECEIVE_EVENTS_LIST:
      return {
        ...payload,
      }
    case EVENT_SAVED:
      return {
        ...state,
        ...payload,
      }
    case EVENT_ATTENDANCE_UPDATED: {
      const { is_attending, number_attending } = state[payload.id]
      let updatedAttendanceCount = parseInt(number_attending)
      if (is_attending != payload.is_attending) {
        updatedAttendanceCount = payload.is_attending
          ? parseInt(number_attending) + 1
          : parseInt(number_attending) - 1
      }
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          is_attending: payload.is_attending,
          number_attending: updatedAttendanceCount,
          ar_payment_id:
            true == payload.is_attending && state[payload.id].ar_payment_id
              ? state[payload.id].ar_payment_id
              : '',
        },
      }
    }

    case EVENT_PAYMENT_UPDATE:
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          ar_payment_id: payload.ar_payment_id,
        },
      }

    case EVENT_DELETED:
      return Object.values(state).reduce((obj, event) => {
        if (event.id === payload) return obj
        return { ...obj, [event.id]: event }
      }, {})
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// sagas
export function* getEventsList() {
  const { events } = yield call(Api.getEvents)
  if (events.length) {
    yield put({
      type: RECEIVE_EVENTS_LIST,
      payload: Object.values(events).reduce(
        (obj, event) => ({
          ...obj,
          [event.id]: event,
        }),
        {},
      ),
    })
  }
}

export function* getEventsListWithSettings() {
  const { events, settings } = yield call(Api.getEventsWithSettings)
  let actions = []
  if (events.length) {
    actions.push({
      type: RECEIVE_EVENTS_LIST,
      payload: Object.values(events).reduce(
        (obj, event) => ({
          ...obj,
          [event.id]: {
            type: 'Event',
            ...event,
          },
        }),
        {},
      ),
    })
  }
  if (Object.keys(settings).length) {
    actions.push(addSettings({ event: settings }))
    actions.push(addCustomContent({ events: settings.custom_text }))
  }
  yield all(actions.map(a => put(a)))
}

export function* updateAttendance({ payload }) {
  yield call(Api.saveEventSettings, {
    event_id: payload.id,
    attending: payload.is_attending,
  })
  yield put({
    type: EVENT_ATTENDANCE_UPDATED,
    payload,
  })
  yield typeof payload.callback === 'function' && payload.callback()
}

export function* deleteMyEvent({ payload }) {
  yield call(Api.deleteEvent, { event_id: payload })
  yield put({ type: EVENT_DELETED, payload })
}

export function* postSaveEvent({ payload, callback }) {
  const formData = new FormData()

  payload.imageData &&
    formData.append('crop_data', JSON.stringify(payload.imageData.crop_data))

  payload.imageData &&
    Object.values(payload.imageData.event_image).map(image => {
      return formData.append('event_image', image)
    })

  let image = ''
  if (payload.imageData) {
    const { event_image } = yield call(Api.saveEventImage, formData)
    image = { ...event_image }
  }

  const payloadData = {
    ...payload,
    temp_event_image_id: image.temp_file_id || '',
  }
  const { message } = yield call(Api.saveEvent, payloadData)

  // we need to always load updated events because if event auto approve is set to NO then we are not getting event object in response and save event doesnt return proper height and width for image_url
  yield put(requestEventsListWithSettings())
  typeof callback === 'function' && callback(message)
}

export function* watchDeleteEvents() {
  yield takeEvery(
    DELETE_EVENT,
    createRequestSaga(deleteMyEvent, {
      key: 'events',
      message: __('Deleting event'),
      successMessage: __('Event successfully deleted'),
      write: true,
    }),
  )
}

export function* watchUpdateAttendance() {
  yield takeEvery(
    UPDATE_EVENT_ATTENDANCE,
    createRequestSaga(updateAttendance, {
      key: 'events',
      message: __('Updating event'),
      successMessage: __('Event attendance updated'),
      write: true,
    }),
  )
}

export function* watchRequestEvents() {
  yield takeLatest(
    REQUEST_EVENTS_LIST,
    createRequestSaga(getEventsList, {
      key: 'events',
      message: __('Looking up events'),
    }),
  )
}

export function* watchSaveEvent() {
  yield takeEvery(
    SAVE_EVENT,
    createRequestSaga(postSaveEvent, {
      key: 'events',
      message: __('Saving event'),
      successMessage: __('Event successdully saved'),
      write: true,
      onError: handlePaymentError,
    }),
  )
}

export function* watchRequestEventsWithSettings() {
  yield takeLatest(
    REQUEST_EVENTS_LIST_WITH_SETTINGS,
    createRequestSaga(getEventsListWithSettings, {
      key: 'events',
      message: __('Looking up events'),
    }),
  )
}

export function* handlePaymentError(err) {
  if (err.code == 90033) {
    yield put(addError({ key: 'events', message: err }))
    return
  }
  yield call(handleResponseError, err, { key: 'events' })
}

export function* saveEventWithPayment({ payload }) {
  const { callback, ...rest } = payload
  const response = yield call(Api.authCaptureEventPayment, rest)
  const { event_details, payments } = response

  if (payments && event_details) {
    yield put({
      type: POSTED_PAYMENT,
      payload: {
        ...payments,
        paymentTypeId: payments?.payment_type_id,
        paymentAccountId: payments?.customer_payment_account_id,
      },
    })

    yield put({
      type: EVENT_PAYMENT_UPDATE,
      payload: {
        id: event_details?.event_id,
        ar_payment_id: payments?.id,
      },
    })
    yield put({
      type: EVENT_ATTENDANCE_UPDATED,
      payload: {
        id: event_details.event_id,
        is_attending: event_details.attending,
      },
    })
  }

  if (typeof callback === 'function') {
    yield callback(response)
  }
}

export function* watchSaveEventWithPayment() {
  yield takeLatest(
    EVENT_AUTH_CAPTURE_PAYMENT,
    createRequestSaga(saveEventWithPayment, {
      key: 'events',
      message: __('Updating Event'),
      successMessage: __('Updated'),
      onError: handlePaymentError,
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestEvents,
  watchSaveEvent,
  watchUpdateAttendance,
  watchDeleteEvents,
  watchRequestEventsWithSettings,
  watchSaveEventWithPayment,
]
